import React, { createContext, useContext, useEffect, useMemo, useState, ReactNode } from 'react';
import { authService } from '../services/authService';
import { ApiService } from '../services/api';
import { useAuth } from './AuthContext';

interface RequestsContextType {
  pendingRequestsCount: number;
  setPendingRequestsCount: (count: number) => void;
  updatePendingCount: (delta: number) => void;
  refreshPendingRequestsCount: () => Promise<void>;
}

const RequestsContext = createContext<RequestsContextType | undefined>(undefined);

export const useRequests = () => {
  const context = useContext(RequestsContext);
  if (context === undefined) {
    throw new Error('useRequests must be used within a RequestsProvider');
  }
  return context;
};

interface RequestsProviderProps {
  children: ReactNode;
}

export const RequestsProvider: React.FC<RequestsProviderProps> = ({ children }) => {
  const { isAuthenticated, user, currentUser } = useAuth();
  const [pendingRequestsCount, setPendingRequestsCount] = useState(0);

  const isOwnerRole = useMemo(() => {
    const roleFromMe = currentUser?.role;
    if (typeof roleFromMe === 'string' && roleFromMe.trim()) {
      return roleFromMe.trim().toUpperCase() === 'OWNER';
    }
    const p: any = user?.profile || {};
    const direct = [
      p?.role, p?.teamRole, p?.team_role, p?.userRole, p?.user_role, p?.portalRole, p?.portal_role,
    ].find((v) => typeof v === 'string' && v.trim());
    if (direct) return String(direct).toUpperCase() === 'OWNER';
    const roles = Array.isArray(p?.roles) ? p.roles : null;
    if (roles && roles.some((r: any) => String(r).toUpperCase() === 'OWNER')) return true;
    return false;
  }, [currentUser?.role, user?.profile]);

  const refreshPendingRequestsCount = async () => {
    if (!isAuthenticated || !isOwnerRole) {
      setPendingRequestsCount(0);
      return;
    }
    const token = authService.getAccessToken();
    if (!token) {
      setPendingRequestsCount(0);
      return;
    }
    try {
      const resp = await ApiService.listRequests(token, { role: 'consultant', status: 'PENDING' });
      setPendingRequestsCount(Array.isArray(resp.data) ? resp.data.length : 0);
    } catch {
      // Keep prior count if refresh fails
    }
  };

  useEffect(() => {
    void refreshPendingRequestsCount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isOwnerRole]);

  const updatePendingCount = (delta: number) => {
    setPendingRequestsCount(prev => Math.max(0, prev + delta));
  };

  const value = {
    pendingRequestsCount,
    setPendingRequestsCount,
    updatePendingCount,
    refreshPendingRequestsCount,
  };

  return (
    <RequestsContext.Provider value={value}>
      {children}
    </RequestsContext.Provider>
  );
};
